import { motion, AnimatePresence } from "framer-motion";
import { FiLogOut, FiX } from "react-icons/fi";
import { createPortal } from "react-dom";

interface LogoutConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

export default function LogoutConfirmModal({ isOpen, onClose, onConfirm }: LogoutConfirmModalProps) {
    const modalContent = (
        <AnimatePresence mode="wait">
            {isOpen && (
                <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    {/* Backdrop Overlay */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 cursor-pointer bg-black/20"
                    />

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden relative border border-gray-100 z-10"
                    >
                        {/* Close Button */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-full z-20"
                        >
                            <FiX size={20} />
                        </button>

                        <div className="p-8 flex flex-col items-center text-center">
                            {/* Icon Container */}
                            <div className="w-16 h-16 rounded-2xl bg-[#B08D55]/10 flex items-center justify-center mb-6 transform rotate-3">
                                <FiLogOut size={32} className="text-[#B08D55]" />
                            </div>

                            {/* Text Content */}
                            <h3 className="text-2xl font-bold text-gray-900 mb-2 font-poppins">
                                Ready to Leave?
                            </h3>

                            <p className="text-gray-500 text-sm leading-relaxed mb-8">
                                Are you sure you want to log out? You'll need to sign back in to access your dashboard.
                            </p>

                            {/* Action Buttons */}
                            <div className="flex flex-col gap-3 w-full">
                                <button
                                    onClick={onConfirm}
                                    className="w-full py-3.5 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 shadow-lg shadow-red-200 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                                >
                                    <FiLogOut size={18} />
                                    Logout
                                </button>
                                
                                <button
                                    onClick={onClose}
                                    className="w-full py-3.5 rounded-xl font-bold text-gray-600 bg-gray-50 hover:bg-gray-100 transition-all active:scale-[0.98]"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );

    if (typeof document === 'undefined') return null;

    return createPortal(modalContent, document.body);
}
